import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store/useAuthStore";
import { authClient } from "@/lib/auth-client";
import { getSupportActor } from "@/utils/supportSession";
import { resetMixpanel, trackSessionEnd } from "@/utils/mixpanel-utils";
import SettingsSheet, { type SettingsSection } from "./workspace/SettingsSheet";
import { UserRound } from "lucide-react";

/**
 * The sidebar's single entry to profile settings, for teachers and students.
 * Input:  onOpen -- told when the settings sheet opens, so a mobile drawer
 *         holding this entry can close out of its way.
 */
const ProfileDropdown = ({ onOpen }: { onOpen?: () => void }) => {
  const [section, setSection] = useState<SettingsSection | null>(null);
  const navigate = useNavigate();
  const { user, clearAuth } = useAuthStore();
  // Reactive, like the banner. A plain module read is correct after the redeem
  // page's full reload (the marker is seeded from localStorage at module load),
  // but if syncSession establishes it after this component mounts, a
  // non-reactive read leaves Logout and the profile editors visible for that
  // render -- with the cookie already impersonating. The session hook is the
  // authoritative half; the marker covers the window where the hook has not
  // resolved yet.
  const { data: supportSessionData } = authClient.useSession();
  const supportSession = Boolean(
    (
      supportSessionData?.session as
        | { impersonatedBy?: string | null }
        | undefined
    )?.impersonatedBy || getSupportActor(),
  );

  const handleLogout = async () => {
    if (user) {
      trackSessionEnd({
        reason: "user_logout",
      });
      resetMixpanel();
    }
    await clearAuth();
    navigate("/");
  };

  return (
    <div className="et-product-surface et-profile-sidebar">
      <button
        type="button"
        className="et-profile-trigger"
        onClick={() => {
          setSection(supportSession ? "audio" : "account");
          onOpen?.();
        }}
        aria-haspopup="dialog"
        aria-label="Profile and settings"
      >
        <UserRound size={18} />
        <span>Profile & settings</span>
      </button>

      {section && (
        <SettingsSheet
          initialSection={section}
          name={user?.name || user?.username || "Your account"}
          onClose={() => setSection(null)}
          onLogout={handleLogout}
          supportSession={supportSession}
        />
      )}
    </div>
  );
};

export default ProfileDropdown;
